import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Paper, Alert, Snackbar } from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import PhotoUpload from '../Common/PhotoUpload';
import PersonalInfoFields from '../PersonalInfoFields';
import { useProfileForm } from '../../hooks/useProfileForm';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useImageUpload } from '../../hooks/useImageUpload';
import AdminApi from 'Service/Admin.api';
import authApi from 'Service/Auth.api';
import { UserInitialStateType } from 'DataTypes/Redux';
import { useSelector } from 'react-redux';
// Removed SkillArr import to use dynamic API data
import { useDispatch } from 'react-redux';
import { setAlertData, startLoading, stopLoading } from '../../../../Redux/Screen/Action';
import { setMyInfo } from '../../../../Redux/User/Action';
import { FormateMyInfoData } from 'Pages/Login/Formatter';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from 'Utils/Common';

const ProfileForm: React.FC = () => {
  const {
    formData,
    errors,
    isDirty,
    isSubmitting,
    updateField,
    setErrors,
    setSubmitting,
    resetForm,
    updateFormData,
    replaceFormData,
  } = useProfileForm();

  const { validateField, validateForm, isFormValid } = useFormValidation();
  const { uploadImage, uploading: imageUploading } = useImageUpload();
  const dispatch = useDispatch<any>();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const baseUrl = process.env.REACT_APP_BASE_URL || '';

  // Expertise State
  const [availableSkills, setAvailableSkills] = useState<any[]>([]);
  // Document Upload State
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
  const [documentsUploaded, setDocumentsUploaded] = useState<boolean>(false);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await AdminApi.getSkills();
        if (response && response.data) {
          setAvailableSkills(response.data);
        }
      } catch (error) {
        console.error("Error fetching skills for mapping:", error);
      }
    };
    fetchSkills();
    fetchPProfileData();
    updateFormData({
      profilePhoto: myInfo?.profileImageUrl || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfo?.id]);


  const fetchPProfileData = async () => {
    try {
      if (myInfo?.id != null) {
        const response = await AdminApi.getProfile(String(myInfo.id));
        const api = response?.data;

        if (api) {
          const fullName = [api.firstName, api.lastName].filter(Boolean).join(' ').trim();
          const isoDate = api.dateOfBirth ? new Date(api.dateOfBirth) : null;
          const yyyyMmDd = isoDate ? `${isoDate.getFullYear()}-${String(isoDate.getMonth() + 1).padStart(2, '0')}-${String(isoDate.getDate()).padStart(2, '0')}` : '';

          const organisationName = api?.corporateOrg?.sName || '';

          const skillsArray = Array.isArray(api.professionalSkills)
            ? api.professionalSkills
              .map((s: any) => String(s?.skillName || '').trim())
              .filter(Boolean)
            : [];
          const skillsString = skillsArray.join(', ');

          // Transform API skills to skillEntries format
          const skillEntries = Array.isArray(api.professionalSkills)
            ? api.professionalSkills.map((skill: any, index: number) => ({
              id: `skill_${Date.now()}_${index}`,
              skillName: skill.skillName || '',
              experienceYears: skill.numberOfExperienceYears || 0,
              panIndia: skill.servesPanIndia || false,
              selectedStates: skill.servesStates ? skill.servesStates.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
              description: skill.description || '',
            }))
            : [];

          const uploadedCertificates = Array.isArray(api.professionalCertificates)
            ? api.professionalCertificates.map((c: any) => ({
              id: c.pkProfessionalCertificate,
              file: c.file,
              uploadedAt: c.uploadedAt
            }))
            : [];
          replaceFormData({
            name: fullName || '',
            email: api.email || api.userName || '',
            phone: api.phoneNumber || '',
            address: api.address || '',
            city: api.city || '',
            state: api.state || '',
            postalCode: api.postalCode || '',
            dateOfBirth: yyyyMmDd,
            description: api.description || '',
            profilePhoto: api.profilePicturePath
              ? `${baseUrl}${api.profilePicturePath.startsWith('/') ? '' : '/'}${api.profilePicturePath}`
              : undefined,
            highestQualification: api.highestQualification || '',
            skills: skillsString,
            skillsChips: skillsArray,
            skillEntries: skillEntries,
            organisationName,
            uploadedCertificates
          });
        }
      } else {
        throw new Error('User ID is missing');
      }
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }
  }

  const handleFieldChange = useCallback((name: string, value: string | any[]) => {
    updateField(name, value);
  }, [updateField]);

  const handleFieldBlur = useCallback((field: string) => {
    const error = validateField(field, formData[field as keyof typeof formData] as string);
    if (error) {
      setErrors({ ...errors, [field]: error });
    }
  }, [validateField, formData, errors, setErrors]);

  const handlePhotoUpload = async (file: File) => {
    try {

      if (!file) {
        console.error('No file selected for upload.');
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        dispatch(setAlertData({
          alertMessage: `The file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
          alertVariant: "error",
          openAlert: true,
        }));
        return;
      }

      const fd = new FormData();
      fd.append("file", file, file.name);
      // Array.from(fd.entries()).forEach(([k, v]) => {
      //   console.log("FormData entry:", k, v);
      // });
      const uploadedUrl = await AdminApi.UploadProfilePicture(file, String(myInfo.id));
      const rawUrl = uploadedUrl?.data?.profilePictureUrl || '';

      // 👇 Attach base URL only if not already a full URL
      const fullUrl = rawUrl.startsWith('http')
        ? rawUrl
        : `${baseUrl}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;

      // updateFormData({ profilePhoto: uploadedUrl.data.profilePictureUrl });
      updateFormData({ profilePhoto: fullUrl });

      dispatch(setMyInfo({
        ...myInfo,
        profileImageUrl: fullUrl,
      }));
      // updateFormData({ profilePhoto: uploadedUrl });
    } catch (error) {
      console.error('Photo upload failed:', error);
      throw error;
    }
  }

  const handleDocumentSubmission = async () => {
    if (selectedDocuments.length === 0) {
      dispatch(setAlertData({
        alertMessage: "Please select at least one file.",
        alertVariant: "error",
        openAlert: true,
      }));
      return;
    }

    const oversizedFiles = selectedDocuments.filter(file => file.size > MAX_FILE_SIZE_BYTES);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(", ");
      dispatch(setAlertData({
        alertMessage: `The following files exceed the ${MAX_FILE_SIZE_MB}MB limit: ${fileNames}`,
        alertVariant: "error",
        openAlert: true,
      }));
      return;
    }

    const uploadData = new FormData();
    // Use email and mobile from formData or myInfo
    uploadData.append("emailId", formData.email || "");
    uploadData.append("mobileNumber", formData.phone || myInfo?.phone || "");

    selectedDocuments.forEach((file) => {
      uploadData.append(`files`, file);
    });

    try {
      dispatch(startLoading({ loading: true, loadtext: "Uploading Documents..." }));
      const response = await authApi.uploadProfessionalDocuments(uploadData);
      if (response?.success) {
        dispatch(stopLoading());
        setSelectedDocuments([]);
        setDocumentsUploaded(true);
        dispatch(setAlertData({
          alertMessage: "Documents Uploaded Successfully",
          alertVariant: "success",
          openAlert: true,
        }));
      } else {
        dispatch(stopLoading());
        dispatch(setAlertData({
          alertMessage: "Upload failed. Please try again.",
          alertVariant: "error",
          openAlert: true,
        }));
      }
    } catch (error) {
      dispatch(stopLoading());
      dispatch(setAlertData({
        alertMessage: "Upload failed. Please try again.",
        alertVariant: "error",
        openAlert: true,
      }));
      console.error("Error uploading documents:", error);
    }
  };


  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Step 1️⃣: Validate form
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (!isFormValid(validationErrors)) {
      dispatch(setAlertData(
        {
          alertMessage: 'Please fix the errors and try again.',
          alertVariant: 'error',
          openAlert: true,
        }
      ));
      return;
    }

    setSubmitting(true);

    try {
      // Step 2️⃣: Parse full name into firstName and lastName
      const nameParts = (formData.name || "").trim().split(" ");
      const FirstName = nameParts[0] || "";
      const LastName = nameParts.slice(1).join(" ") || "";

      // Step 3️⃣: Conditional payload building based on userType
      let payload;

      if (myInfo?.userType === "PROFESSIONAL") {
        // ✅ PROFESSIONAL payload
          const Skills = (formData.skillEntries || [])
          .filter(skill => skill.skillName)
          .map(skill => {
            const matchedSkill = availableSkills.find(
              s => s.label.toLowerCase() === skill.skillName.toLowerCase()
            );

            return {
              SkillId: matchedSkill ? matchedSkill.id : (matchedSkill ? matchedSkill.PkSkillDetails : null),
              Rating: 0,
              NumberOfExperienceYears: skill.experienceYears || 0,
              Description: skill.description || "",
              ProfessionalFeesPerCallingRs: 0,
              ServesPanIndia: skill.panIndia || false,
              ...(skill.panIndia ? {} : { ServesStates: skill.selectedStates || [] }),
            };
          });

        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          Address: formData.address,
          ContactNumber: formData.phone,
          City: formData.city,
          State: formData.state,
          PostalCode: formData.postalCode,
          DateOfBirth: formData.dateOfBirth,
          ProfilePicturePath: formData.profilePhoto || "",
          HighestQualification: formData.highestQualification || "",
          Description: formData.description || "",
          Skills,
        };
      }
      // else if (myInfo?.userType === "USER" || myInfo?.userType === "CORPORATEUSER") {
      //   // ✅ USER payload (minimal)
      //   payload = {
      //     FirstName,
      //     LastName,
      //     Email: formData.email,
      //     ContactNumber: formData.phone,
      //     DateOfBirth: formData.dateOfBirth,
      //     Address: formData.address,
      //     State: formData.state,
      //     City: formData.city,
      //     PostalCode: formData.postalCode,
      //   };
      // }

      else {
        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          ContactNumber: formData.phone,
          DateOfBirth: formData.dateOfBirth,
          Address: formData.address,
          State: formData.state,
          City: formData.city,
          PostalCode: formData.postalCode,
        };
      }


      // Step 5️⃣: API Call
      const response = await AdminApi.ProfileUpdate(
        String(myInfo?.id),
        payload,
        String(myInfo?.userType)
      );

      // setShowSuccessMessage(true);
      if (response?.success) {
        dispatch(setAlertData(
          {
            alertMessage: 'Profile Updated successfully!',
            alertVariant: 'success',
            openAlert: true,
          }
        ));

        // Update Redux State
        dispatch(setMyInfo({
          ...myInfo,
          id: myInfo?.id,
          firstname: FirstName,
          lastname: LastName,
          phone: formData.phone,
        }))
      }
    } catch (error) {
      console.error("❌ Save failed:", error);
      // setShowErrorMessage(true);
      dispatch(setAlertData(
        {
          alertMessage: 'Failed to update profile. Please try again.',
          alertVariant: 'error',
          openAlert: true,
        }
      ));
    } finally {
      setSubmitting(false);
    }
  }, [validateForm, formData, setErrors, isFormValid, setSubmitting, myInfo?.id, myInfo?.userType]);


  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (confirmCancel) {
        resetForm();
      }
    }
  }, [isDirty, resetForm]);


  return (
    <>
      <Box
        sx={{
          height: ['PROFESSIONAL', 'CORPORATEUSER'].includes(myInfo?.userType)
            ? '75vh'
            : '65vh',
          //         height: myInfo?.userType === 'PROFESSIONAL'
          // ? '75vh'
          // : myInfo?.userType === 'CORPORATEUSER'
          //   ? '75vh'
          //   : '65vh',
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Scrollable Form Content */}
        <Paper
          component="form"
          id="profile-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,               // take remaining space
            overflowY: "auto",     // scroll inside
            // p: { xs: 2, sm: 3 }
          }}
        >
          <PhotoUpload
            currentPhoto={typeof formData.profilePhoto === 'string' ? formData.profilePhoto : undefined}
            onPhotoUpload={handlePhotoUpload}
            loading={imageUploading}
          />

          <PersonalInfoFields
            formData={formData}
            errors={errors}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            // New Document Upload Props
            onFileChange={setSelectedDocuments}
            onUpload={handleDocumentSubmission}
            files={selectedDocuments}
            isUploaded={documentsUploaded}
          />
        </Paper>

        {/* Sticky Footer with Actions */}
        <Box
          sx={{
            borderTop: "1px solid #dee2e6",
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            backgroundColor: "#f8f9fa",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={isSubmitting || !isDirty}
            sx={{
              backgroundColor: '#007bff',
              color: '#fff',
              '&:hover': { backgroundColor: '#0056b3' },
              '&:disabled': { backgroundColor: '#6c757d' }
            }}
            form="profile-form" // ensures it triggers form submit
          >
            {isSubmitting ? "Saving..." : "Save Profile"}
          </Button>

          <Button
            type="button"
            variant="outlined"
            startIcon={<Cancel />}
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              color: '#6c757d',
              borderColor: '#dee2e6',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#6c757d',
                color: '#495057',
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>



    </>
  );



};

export default ProfileForm;